import styled from 'styled-components'
import { useState } from 'react'
import { fetcher } from '../utils/fetcher'
import {
  breakPoints,
  breakPointsCss,
  colors,
  measurments,
} from '../utils/config'
import SponsorForm, { SponsorInput } from '../components/SponsorForm'
import { Button, Icon, ResponsiveImage } from '@bm-js/atlascloud-ui'
import Input from '../components/Input'
import Select from '../components/Select'
import Loader from '../components/Loader'
import DeleteConfirmation from '../components/DeleteConfirmation'
import { Title } from 'react-head'
import { useMemo } from 'react'
import { LucideIconName, Sponsor } from '@bm-js/atlascloud-shared'
import { usePopup, useToast, useToken, useWebsite } from '../utils/hooks'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useTr } from '../utils/useTr'

const StyledSponsors = styled.div`
  .sponsors-container {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;

    .sponsor {
      border-radius: ${measurments.borderRadius}px;
      overflow: hidden;
      background: white;
      box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
      padding: 1rem;
      img {
        width: 100%;
        height: 200px;
        object-fit: contain;
      }
      .sponsor-text-content {
        margin-top: 1rem;
        .actions {
          margin-top: 0.5rem;
          gap: 0.5rem;
        }
      }
    }
    &.list {
      display: block;
      .sponsor {
        background: none;
        box-shadow: none;
        img {
          width: 50px;
          height: 50px;
          object-fit: contain;
          margin: 0;
        }
        display: grid;
        grid-template-columns: 50px 1fr;
        gap: 3rem;
        align-items: center;
        .sponsor-text-content {
          margin-top: 0;
          justify-items: flex-start;
          display: grid;
          align-items: center;
          grid-template-columns: 1fr 1fr;
        }
        .actions {
          justify-self: flex-end;
        }
        position: relative;
        &:after {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: 1px;
          background: ${colors.blackAlpla300};
          content: '';
        }
      }
    }
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
    .sponsors-container {
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
    }
  }
`

const Sponsors = () => {
  const toast = useToast()
  const token = useToken()
  const { atlasWebsite, refetchAtlasWebsite, triggerRebuild } = useWebsite()
  const { closePopup, setPopup } = usePopup()
  const { tr } = useTr()

  const [loading, setLoading] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [displayMode, setDisplayMode] = useState('list')

  const createNewSponsor = async (
    sponsor: SponsorInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: 'customer/create-sponsor',
      method: 'POST',
      body: sponsor,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.sponsorsFailedToSave))
      errCb()
      return
    }
    toast.success(tr(TrKey.sponsorSaved))
    cb()
    closePopup()
    refetchAtlasWebsite()
    triggerRebuild()
  }

  const updateSponsor = async (
    sponsor: SponsorInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: `customer/update-sponsor/${sponsor._id}`,
      method: 'POST',
      body: sponsor,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.sponsorsFailedToSave))
      errCb()
      return
    }
    toast.success(tr(TrKey.sponsorUpdated))
    cb()
    closePopup()
    refetchAtlasWebsite()
    triggerRebuild()
  }

  const deleteInit = (sponsor: Sponsor) => {
    setPopup(
      <DeleteConfirmation
        item={sponsor}
        deleteKey='name'
        handleDelete={deleteSponsor}
        handleCancel={closePopup}
        name={tr(TrKey.theSponsor)}
      />
    )
  }

  const deleteSponsor = async (sponsor: Sponsor, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `customer/delete-sponsor/${sponsor._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      cb()
      setLoading(false)
      toast.error(tr(TrKey.sponsorsFailedToDelete))
      return
    }
    cb()
    closePopup()
    toast.success(tr(TrKey.sponsorDeleted))
    refetchAtlasWebsite()
    triggerRebuild()
  }
  const updateInit = (sponsor: SponsorInput) => {
    setPopup(<SponsorForm handleSubmit={updateSponsor} data={sponsor} />)
  }

  const createInit = () => {
    setPopup(<SponsorForm handleSubmit={createNewSponsor} />)
  }

  const filteredSponsors = useMemo(() => {
    //Text filtering
    const s = searchString.toLocaleLowerCase()
    let matches: Sponsor[] = []
    if (s.length > 0) {
      atlasWebsite?.sponsors.forEach((object) => {
        if (object.name) {
          const search = object.name.toLowerCase().search(s)
          if (search !== -1) matches.push(object)
        }
      })
    } else {
      matches = atlasWebsite?.sponsors || []
    }
    const sortedMatches = matches.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    return sortedMatches
  }, [atlasWebsite, searchString])

  const title = useMemo(() => {
    return tr(TrKey.sponsors) + ' | Atlascloud'
  }, [tr])

  if (!atlasWebsite || loading)
    return (
      <StyledSponsors>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledSponsors>
    )

  const tablet = window.innerWidth < breakPoints.tablet

  return (
    <StyledSponsors className='content'>
      <Title>{title}</Title>
      <div className=' headers jcsb mbottom1 aife flex'>
        <h1 className='m0 l primblue'>{tr(TrKey.sponsors)}</h1>
        <Input
          noMargin
          type='text'
          placeholder={tr(TrKey.sponsorsSearchPlaceholder)}
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
        />
        <Select
          noMargin
          value={displayMode}
          onChange={(e) => setDisplayMode(e.target.value)}
        >
          <option value='list'>{tr(TrKey.list)}</option>
          <option value='grid'>{tr(TrKey.grid)}</option>
        </Select>

        <Button onClick={createInit}>{tr(TrKey.add)}</Button>
      </div>
      <div className={`sponsors-container ${displayMode}`}>
        {filteredSponsors.length ? (
          filteredSponsors.map((sponsor) => (
            <div className='sponsor' key={sponsor._id}>
              <ResponsiveImage file={sponsor.file || undefined} />
              <div className='sponsor-text-content'>
                <a className='m0' href={sponsor.url} target='_blank'>
                  {sponsor.name}
                </a>
                <div className='actions flex'>
                  <Button
                    disabled={loading}
                    onClick={() => {
                      updateInit(sponsor)
                    }}
                    withIcon={tablet}
                    className='text-white'
                  >
                    {tablet ? (
                      <Icon name={LucideIconName.Pen} className='size-5' />
                    ) : (
                      tr(TrKey.edit)
                    )}
                  </Button>
                  <Button
                    danger
                    disabled={loading}
                    onClick={() => deleteInit(sponsor)}
                    withIcon={tablet}
                    className='text-white'
                  >
                    {tablet ? (
                      <Icon name={LucideIconName.Trash2} className='size-5' />
                    ) : (
                      tr(TrKey.delete)
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>{tr(TrKey.sponsorsNoSponsorsFound)}</p>
        )}
      </div>
    </StyledSponsors>
  )
}

export default Sponsors
